/** Haversine great-circle distance in nautical miles */
export function haversineDistance(pos1, pos2) {
  const R = 3440.065; // Earth radius in nautical miles
  const lat1 = pos1.lat * Math.PI / 180;
  const lat2 = pos2.lat * Math.PI / 180;
  const dLat = (pos2.lat - pos1.lat) * Math.PI / 180;
  const dLng = (pos2.lng - pos1.lng) * Math.PI / 180;

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Linear interpolation between two {lat,lng} positions, t in [0,1] */
export function lerpPosition(a, b, t) {
  let dLng = b.lng - a.lng;

  // Handle dateline crossing - go the short way
  if (dLng > 180) dLng -= 360;
  else if (dLng < -180) dLng += 360;

  let lng = a.lng + dLng * t;

  // Normalize result to [-180, 180]
  if (lng > 180) lng -= 360;
  else if (lng < -180) lng += 360;

  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lng
  };
}

/** Interpolate along a polyline of waypoints, t in [0,1] */
export function interpolatePolyline(waypoints, t) {
  if (waypoints.length < 2) return waypoints[0];
  if (t <= 0) return { ...waypoints[0] };
  if (t >= 1) return { ...waypoints[waypoints.length - 1] };

  // Build segment lengths
  const segments = [];
  let totalDist = 0;
  for (let i = 1; i < waypoints.length; i++) {
    const d = haversineDistance(waypoints[i - 1], waypoints[i]);
    segments.push(d);
    totalDist += d;
  }

  let targetDist = t * totalDist;
  for (let i = 0; i < segments.length; i++) {
    if (targetDist <= segments[i]) {
      const segT = segments[i] === 0 ? 0 : targetDist / segments[i];
      return lerpPosition(waypoints[i], waypoints[i + 1], segT);
    }
    targetDist -= segments[i];
  }
  return { ...waypoints[waypoints.length - 1] };
}

/** Bearing in degrees (0=N, 90=E) from pos1 to pos2 */
export function bearing(pos1, pos2) {
  const lat1 = pos1.lat * Math.PI / 180;
  const lat2 = pos2.lat * Math.PI / 180;
  const dLng  = (pos2.lng - pos1.lng) * Math.PI / 180;

  const x = Math.sin(dLng) * Math.cos(lat2);
  const y = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const b = Math.atan2(x, y) * 180 / Math.PI;
  return (b + 360) % 360;
}

/** Clamp a number between min and max */
export function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

/**
 * Normalize a polyline for Leaflet to handle antimeridian crossing.
 * When consecutive points cross the 180° line, adjust longitudes
 * so Leaflet draws the short path across the dateline.
 */
export function normalizePolylineForDateline(waypoints) {
  if (waypoints.length < 2) return waypoints;

  const result = [{ lat: waypoints[0].lat, lng: waypoints[0].lng }];

  for (let i = 1; i < waypoints.length; i++) {
    const prev = result[i - 1];
    const curr = waypoints[i];

    let lng = curr.lng;

    // Check if crossing dateline
    const dLng = curr.lng - prev.lng;

    // If the difference is more than 180°, we're going the long way
    // Adjust to go the short way across the dateline
    if (dLng > 180) {
      lng = curr.lng - 360;
    } else if (dLng < -180) {
      lng = curr.lng + 360;
    }

    result.push({ lat: curr.lat, lng });
  }

  return result;
}
